export const KING_OF_THE_HILL_CENTER = ['d4', 'd5', 'e4', 'e5'];

export const VARIANTS = {
  standard: {
    id: 'standard',
    label: 'Standard',
    description: 'Classic chess with standard rules.',
    playable: true,
    leaderboard: true
  },
  chess960: {
    id: 'chess960',
    label: 'Chess960',
    description: 'Randomized back-rank pieces with standard piece movement.',
    playable: true,
    leaderboard: true
  },
  kingOfTheHill: {
    id: 'kingOfTheHill',
    label: 'King of the Hill',
    description: 'Reach the center with your king to win.',
    playable: true,
    leaderboard: true
  },
  threeCheck: {
    id: 'threeCheck',
    label: '3-Check',
    description: 'Deliver three checks to your opponent to win.',
    playable: true,
    leaderboard: true
  },
  atomic: {
    id: 'atomic',
    label: 'Atomic',
    description: 'Captures explode adjacent pieces; checkmate the king to win.',
    playable: false,
    leaderboard: true
  },
  crazyhouse: {
    id: 'crazyhouse',
    label: 'Crazyhouse',
    description: 'Drop captured pieces back onto the board.',
    playable: false,
    leaderboard: true
  },
  bughouse: {
    id: 'bughouse',
    label: 'Bughouse',
    description: 'Two boards, four players, and piece passing between partners.',
    playable: false,
    leaderboard: true
  },
  fourPlayer: {
    id: 'fourPlayer',
    label: '4-Player',
    description: 'Four-player chess on an extended board.',
    playable: false,
    leaderboard: true
  },
  fogOfWar: {
    id: 'fogOfWar',
    label: 'Fog of War',
    description: 'Only see squares your pieces can currently reach.',
    playable: false,
    leaderboard: true
  }
};

export const PLAYABLE_VARIANTS = Object.values(VARIANTS).filter((variant) => variant.playable);
export const BACKLOG_VARIANTS = Object.values(VARIANTS).filter((variant) => !variant.playable);

export function isPlayableVariant(variantId) {
  return Boolean(VARIANTS[variantId]?.playable);
}

export function variantAllowsLeaderboard(variantId) {
  return VARIANTS[variantId]?.leaderboard ?? false;
}

function pickRandom(items, random) {
  const index = Math.floor(random() * items.length);
  return items[index];
}

function pickMany(items, count, random) {
  const pool = [...items];
  const picked = [];
  for (let index = 0; index < count; index += 1) {
    const slot = Math.floor(random() * pool.length);
    picked.push(pool.splice(slot, 1)[0]);
  }
  return picked;
}

export function generateChess960Fen(random = Math.random) {
  const files = [0, 1, 2, 3, 4, 5, 6, 7];
  const lightSquares = [1, 3, 5, 7];
  const darkSquares = [0, 2, 4, 6];
  const bishopLight = pickRandom(lightSquares, random);
  const bishopDark = pickRandom(darkSquares, random);

  let remaining = files.filter((file) => file !== bishopLight && file !== bishopDark);
  const queen = pickRandom(remaining, random);
  remaining = remaining.filter((file) => file !== queen);
  const knights = pickMany(remaining, 2, random);
  remaining = remaining.filter((file) => !knights.includes(file)).sort((left, right) => left - right);

  const rank = Array(8).fill('');
  rank[bishopLight] = 'B';
  rank[bishopDark] = 'B';
  rank[queen] = 'Q';
  rank[knights[0]] = 'N';
  rank[knights[1]] = 'N';
  rank[remaining[0]] = 'R';
  rank[remaining[1]] = 'K';
  rank[remaining[2]] = 'R';

  const backRank = rank.join('');
  // chess.js 1.4 only implements standard castling (KQkq → king ±2, rook a/h).
  // Shredder/X-FEN rook-file letters are rejected, so advertise no castling rights
  // until a Chess960-capable engine is wired in.
  return `${backRank.toLowerCase()}/pppppppp/8/8/8/8/PPPPPPPP/${backRank} w - - 0 1`;
}

export function kingSquare(chess, color) {
  const board = chess.board();
  const targetColor = color === 'white' ? 'w' : 'b';

  for (let rank = 0; rank < 8; rank += 1) {
    for (let file = 0; file < 8; file += 1) {
      const piece = board[rank][file];
      if (piece?.type === 'k' && piece.color === targetColor) {
        return `${'abcdefgh'[file]}${8 - rank}`;
      }
    }
  }

  return null;
}

export function isKingOfTheHillWin(chess, color) {
  const square = kingSquare(chess, color);
  return Boolean(square && KING_OF_THE_HILL_CENTER.includes(square));
}
